import JSZip from 'jszip';

/**
 * Decode MIME encoded-word syntax (e.g. =?UTF-8?B?...?= or =?utf-8?Q?...?=)
 */
export const decodeMimeHeader = (rawHeader) => {
  if (!rawHeader) return '';
  
  // Replace encoded-word patterns
  return rawHeader.replace(/=\?([^?]+)\?([BQbq])\?([^?]+)\?=/g, (match, charset, encoding, text) => {
    try {
      if (encoding.toUpperCase() === 'B') {
        // Base64 decoding with UTF-8 byte conversion
        const binaryStr = atob(text);
        const bytes = Uint8Array.from(binaryStr, c => c.charCodeAt(0));
        const decoder = new TextDecoder(charset.toLowerCase() || 'utf-8');
        return decoder.decode(bytes);
      } else if (encoding.toUpperCase() === 'Q') {
        // Quoted-printable
        const clean = text.replace(/_/g, ' ').replace(/=([A-Fa-f0-9]{2})/g, (_, hex) => 
          String.fromCharCode(parseInt(hex, 16))
        );
        return clean;
      }
    } catch (e) {
      console.warn('MIME header decode error:', e);
      return text;
    }
    return match;
  });
};

/**
 * Parse a raw .eml RFC 822 string into a structured email object
 */
export const parseEmlString = (emlText, filename = 'message.eml') => {
  const lines = emlText.split(/\r?\n/);
  const headers = {};
  let currentHeader = null;
  let bodyStartIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.trim() === '') {
      bodyStartIndex = i + 1;
      break;
    }

    // Check if continuation of multi-line header
    if ((line.startsWith(' ') || line.startsWith('\t')) && currentHeader) {
      headers[currentHeader] += ' ' + line.trim();
    } else {
      const colonIdx = line.indexOf(':');
      if (colonIdx !== -1) {
        const key = line.slice(0, colonIdx).trim().toLowerCase();
        const value = line.slice(colonIdx + 1).trim();
        headers[key] = value;
        currentHeader = key;
      }
    }
  }

  const rawBody = bodyStartIndex !== -1 ? lines.slice(bodyStartIndex).join('\n') : '';

  const subject = decodeMimeHeader(headers['subject'] || filename.replace('.eml', ''));
  const sender = decodeMimeHeader(headers['from'] || 'unknown@domain.com');
  const recipient = decodeMimeHeader(headers['to'] || '');
  const dateStr = headers['date'] || new Date().toISOString();
  
  let parsedTimestamp;
  try {
    parsedTimestamp = new Date(dateStr).toISOString();
  } catch {
    parsedTimestamp = new Date().toISOString();
  }

  const contentType = headers['content-type'] || 'text/plain';
  let bodyText = '';
  let bodyHTML = '';

  // Extract boundary if multipart
  if (contentType.includes('multipart/')) {
    const boundaryMatch = contentType.match(/boundary=["']?([^"';]+)["']?/i);
    if (boundaryMatch) {
      const boundary = boundaryMatch[1];
      const parts = rawBody.split(new RegExp(`--${boundary}(?:--)?`));
      for (const part of parts) {
        if (part.includes('text/html')) {
          const partBodyIdx = part.indexOf('\n\n') !== -1 ? part.indexOf('\n\n') : part.indexOf('\r\n\r\n');
          if (partBodyIdx !== -1) {
            bodyHTML = part.slice(partBodyIdx).trim();
          }
        } else if (part.includes('text/plain') && !bodyText) {
          const partBodyIdx = part.indexOf('\n\n') !== -1 ? part.indexOf('\n\n') : part.indexOf('\r\n\r\n');
          if (partBodyIdx !== -1) {
            bodyText = part.slice(partBodyIdx).trim();
          }
        }
      }
    }
  }

  if (!bodyHTML && !bodyText) {
    if (contentType.includes('text/html') || rawBody.includes('<html') || rawBody.includes('<body') || rawBody.includes('<div')) {
      bodyHTML = rawBody;
      bodyText = rawBody.replace(/<[^>]*>?/gm, '').trim();
    } else {
      bodyText = rawBody;
      bodyHTML = `<div><p>${rawBody.replace(/\n/g, '<br/>')}</p></div>`;
    }
  } else if (bodyHTML && !bodyText) {
    bodyText = bodyHTML.replace(/<[^>]*>?/gm, '').trim();
  } else if (bodyText && !bodyHTML) {
    bodyHTML = `<div><p>${bodyText.replace(/\n/g, '<br/>')}</p></div>`;
  }

  return {
    EmailID: `backup-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    Sender: sender,
    Recipient: recipient,
    Subject: subject,
    BodyText: bodyText,
    BodyHTML: bodyHTML,
    Attachments: [],
    IsRead: true,
    IsRecipientRead: true,
    ReadAt: parsedTimestamp,
    DeliveredAt: parsedTimestamp,
    Timestamp: parsedTimestamp,
    IsBackupRestored: true
  };
};

/**
 * Restore emails from a Backup ZIP file containing list.html and messages/*.eml
 */
export const restoreFromZipBackup = async (zipFile, onProgress = null) => {
  const zip = new JSZip();
  const loadedZip = await zip.loadAsync(zipFile);
  const emailsRestored = [];

  const allFiles = Object.keys(loadedZip.files);
  const emlFiles = allFiles.filter(name => name.toLowerCase().endsWith('.eml') && !loadedZip.files[name].dir);

  let processedCount = 0;
  for (const filename of emlFiles) {
    try {
      const fileData = await loadedZip.files[filename].async('string');
      const parsedEmail = parseEmlString(fileData, filename.split('/').pop());
      emailsRestored.push(parsedEmail);
    } catch (err) {
      console.warn(`Error parsing EML file ${filename}:`, err);
    }
    processedCount++;
    if (onProgress) {
      onProgress(Math.round((processedCount / emlFiles.length) * 100));
    }
  }

  // Also check if list.html has metadata we can enhance with
  const listHtmlFile = allFiles.find(name => name.toLowerCase().endsWith('list.html'));
  if (listHtmlFile) {
    try {
      const listHtmlText = await loadedZip.files[listHtmlFile].async('string');
      console.log('Parsed backup list.html file successfully, length:', listHtmlText.length);
    } catch (e) {
      console.warn('list.html parse info:', e);
    }
  }

  return emailsRestored;
};

/**
 * Generate and download a standard Backup ZIP package containing list.html and messages/*.eml
 */
export const exportMailBackupZip = async (emails, currentUser) => {
  const zip = new JSZip();
  const messagesFolder = zip.folder('messages');

  // Filter currentUser emails
  const userEmails = emails.filter(e => {
    const userEmail = (currentUser?.Email || '').toLowerCase();
    const sender = (e.Sender || '').toLowerCase();
    const recipient = (e.Recipient || '').toLowerCase();
    return sender.includes(userEmail) || recipient.includes(userEmail);
  });

  const emailRecords = [];

  // 1. Generate individual .eml files
  userEmails.forEach((email, idx) => {
    const safeSubject = (email.Subject || 'no_subject').replace(/[^a-zA-Z0-9가-힣_-]/g, '_').slice(0, 30);
    const emlFilename = `msg_${idx + 1}_${safeSubject}.eml`;

    const dateHeader = new Date(email.Timestamp || Date.now()).toUTCString();
    const emlContent = [
      `From: ${email.Sender}`,
      `To: ${email.Recipient}`,
      `Date: ${dateHeader}`,
      `Subject: =?UTF-8?B?${btoa(unescape(encodeURIComponent(email.Subject || '')))}?=`,
      `MIME-Version: 1.0`,
      `Content-Type: text/html; charset=UTF-8`,
      `Content-Transfer-Encoding: 8bit`,
      ``,
      email.BodyHTML || `<div><p>${email.BodyText || ''}</p></div>`
    ].join('\r\n');

    messagesFolder.file(emlFilename, emlContent);

    emailRecords.push({
      idx: idx + 1,
      filename: `messages/${emlFilename}`,
      subject: email.Subject || '(제목 없음)',
      sender: email.Sender,
      recipient: email.Recipient,
      date: new Date(email.Timestamp || Date.now()).toLocaleString('ko-KR')
    });
  });

  // 2. Generate list.html
  const listHtmlContent = `<!DOCTYPE html>
<html lang="ko">
<head>
  <meta charset="UTF-8">
  <title>클로우플레이크 메일 백업 목록 (${currentUser?.Name} - ${currentUser?.Email})</title>
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Malgun Gothic", sans-serif; padding: 30px; background-color: #f8fafc; color: #1e293b; }
    h1 { font-size: 22px; color: #0f172a; margin-bottom: 4px; }
    p.meta { font-size: 13px; color: #64748b; margin-bottom: 24px; }
    table { width: 100%; border-collapse: collapse; background: white; border-radius: 12px; overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
    th { background: #f1f5f9; color: #475569; font-size: 12px; font-weight: 700; text-align: left; padding: 12px 16px; border-bottom: 1px solid #e2e8f0; }
    td { padding: 12px 16px; font-size: 13px; border-bottom: 1px solid #f1f5f9; }
    tr:hover { background-color: #f8fafc; }
    a.eml-link { color: #4f46e5; text-decoration: none; font-weight: 600; }
    a.eml-link:hover { text-decoration: underline; }
    .badge { display: inline-block; padding: 2px 8px; font-size: 11px; font-weight: bold; border-radius: 9999px; background: #e0e7ff; color: #4338ca; }
  </style>
</head>
<body>
  <h1>📁 Clouflake Webmail 백업 아카이브</h1>
  <p class="meta">계정: <strong>${currentUser?.Name} (${currentUser?.Email})</strong> | 백업 생성 일시: ${new Date().toLocaleString('ko-KR')} | 총 보관 메일: ${emailRecords.length}건</p>
  
  <table>
    <thead>
      <tr>
        <th style="width: 50px;">No</th>
        <th>메일 제목</th>
        <th>보낸 사람</th>
        <th>받는 사람</th>
        <th>일시</th>
        <th>EML 원본 파일</th>
      </tr>
    </thead>
    <tbody>
      ${emailRecords.map(r => `
        <tr>
          <td>${r.idx}</td>
          <td><strong>${r.subject}</strong></td>
          <td>${r.sender}</td>
          <td>${r.recipient}</td>
          <td>${r.date}</td>
          <td><a class="eml-link" href="${r.filename}">📄 ${r.filename.split('/').pop()}</a></td>
        </tr>
      `).join('')}
    </tbody>
  </table>
</body>
</html>`;

  zip.file('list.html', listHtmlContent);

  // 3. Generate Zip Blob and trigger download
  const content = await zip.generateAsync({ type: 'blob' });
  const downloadUrl = URL.createObjectURL(content);
  const a = document.createElement('a');
  a.href = downloadUrl;
  const nowStr = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  a.download = `clouflake_mail_backup_${currentUser?.Account || 'user'}_${nowStr}.zip`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(downloadUrl);
};
